import React, { useState, useEffect, useCallback } from 'react'
import DriverAvatar from './DriverAvatar'
import { BORG_WARNER_TROPHY } from '../driverPhotos'
import { CountdownCompact } from './CountdownTimer'

const PLAYER_COLORS = [
  '#e53e3e','#3182ce','#38a169','#d69e2e',
  '#805ad5','#dd6b20','#319795','#e53e7a',
  '#4299e1','#48bb78','#ecc94b'
]
const MEDALS = ['🥇','🥈','🥉']
const POS_COLORS = ['#d4a017','#9ca3af','#cd7f32']

interface Driver { number: number; name: string; team?: string }
interface Assignment { playerName: string; drivers: Driver[] }
interface Prizes { first: number; second: number; third: number }
interface LiveEntry { position: number; driverNumber: number; driverName: string; speed: number; location: string; isDNF: boolean }

interface Props {
  assignments: Assignment[]
  prizes: Prizes
  gameId: string
  raceStarted?: boolean
  onStartRace: () => void
  onViewPlayer?: (idx: number) => void
}

export default function PreRaceDashboard({ assignments, prizes, gameId, raceStarted, onStartRace, onViewPlayer }: Props) {
  const totalDrivers = assignments.reduce((n, a) => n + a.drivers.length, 0)

  // ── Optional live standings ───────────────────────────────────────────────
  const [liveEntries, setLiveEntries] = useState<LiveEntry[]>([])
  const [liveUpdated, setLiveUpdated] = useState('')

  const fetchLive = useCallback(async () => {
    try {
      const data = await fetch('/api/race/live').then(r => r.json())
      if (data?.entries?.length) {
        setLiveEntries(data.entries)
        setLiveUpdated(new Date().toLocaleTimeString())
      }
    } catch { /* silent */ }
  }, [])

  useEffect(() => {
    fetchLive()
    const id = setInterval(fetchLive, 15_000)
    return () => clearInterval(id)
  }, [fetchLive])

  // Best driver position per player
  const playerStandings = assignments
    .map((a, idx) => {
      const myNumbers = new Set(a.drivers.map(d => d.number))
      const myEntries = liveEntries.filter(e => myNumbers.has(e.driverNumber) && !e.isDNF)
      const best = myEntries.reduce<LiveEntry | null>((b, e) => (!b || e.position < b.position) ? e : b, null)
      return { idx, playerName: a.playerName, best }
    })
    .filter(p => p.best !== null)
    .sort((a, b) => a.best!.position - b.best!.position)

  const top3 = playerStandings.slice(0, 3)

  return (
    <div style={{ position: 'relative' }}>

      {/* ── Trophy background watermark ── */}
      <img
        src={BORG_WARNER_TROPHY}
        alt=""
        aria-hidden="true"
        style={{
          position: 'fixed', right: 0, top: 0, bottom: 0,
          height: '100vh', width: 'auto', objectFit: 'cover', objectPosition: 'center top',
          zIndex: 0, pointerEvents: 'none',
          maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.5) 35%, rgba(0,0,0,0.75) 100%)',
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.5) 35%, rgba(0,0,0,0.75) 100%)',
          filter: 'brightness(0.35) saturate(0.7)',
        }}
      />

      {/* ── Page content (sits above trophy) ── */}
      <div style={{ position: 'relative', zIndex: 1 }}>

      {/* ── Trophy + Header row ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
        <img
          src={BORG_WARNER_TROPHY}
          alt="Borg Warner Trophy"
          style={{ width: 64, height: 90, objectFit: 'cover', objectPosition: 'center top',
            borderRadius: 8, border: '2px solid rgba(212,160,23,0.4)', flexShrink: 0 }}
          onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
        />
        <div style={{ flex: 1 }}>
          <div className="race-header" style={{ margin: 0, border: 'none', paddingBottom: 0 }}>
            <div className="section-title" style={{ margin: 0 }}>
              🏁 Pre-Race Roster
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
                {assignments.length} players · {totalDrivers} drivers assigned
              </span>
              <CountdownCompact />
              {raceStarted ? (
                <button className="btn btn-primary" onClick={onStartRace}>
                  🔴 Back to Live Race
                </button>
              ) : (
                <button className="btn btn-primary" onClick={onStartRace}>
                  🚦 Start Race Tracking
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Live Standings (shown when race data available) ── */}
      {top3.length > 0 && (
        <div className="card" style={{ marginBottom: '1.2rem', padding: '0.9rem 1.2rem',
          borderTop: '3px solid #d4a017', background: 'rgba(212,160,23,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.7rem' }}>
            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', letterSpacing: 2, fontWeight: 700 }}>
              🏆 LIVE STANDINGS
            </div>
            {liveUpdated && (
              <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.3)' }}>
                Updated {liveUpdated}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
            {top3.map(({ idx, playerName, best }, rank) => (
              <div key={playerName} style={{
                flex: '1 1 180px', background: 'rgba(255,255,255,0.04)',
                borderRadius: 8, padding: '0.6rem 0.9rem',
                borderLeft: `4px solid ${POS_COLORS[rank]}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.4rem' }}>{MEDALS[rank]}</span>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '0.9rem',
                      color: PLAYER_COLORS[idx % PLAYER_COLORS.length] }}>
                      {playerName}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)' }}>
                      P{best!.position} · {best!.driverName} · {best!.speed.toFixed(1)} mph
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Prize strip ── */}
      <div className="race-prize-strip" style={{ marginBottom: '1.5rem' }}>
        <div className="prize-strip-label">💰 PRIZE POOL</div>
        {[
          { label: '🥇 1st Place', val: prizes.first },
          { label: '🥈 2nd Place', val: prizes.second },
          { label: '🥉 3rd Place', val: prizes.third },
        ].map(({ label, val }) => (
          <div className="prize-strip-item" key={label}>
            <div className="prize-strip-pos">{label}</div>
            <div className="prize-strip-val">${val}</div>
          </div>
        ))}
        <div className="prize-strip-item">
          <div className="prize-strip-pos">Total</div>
          <div className="prize-strip-val">${prizes.first + prizes.second + prizes.third}</div>
        </div>
      </div>

      {/* ── Player roster cards ── */}
      {(() => {
        const liveMap = new Map(liveEntries.map(e => [e.driverNumber, e]))
        const hasLive = liveEntries.length > 0
        return (
          <div className="dashboard-grid">
            {assignments.map((a, idx) => {
              const color = PLAYER_COLORS[idx % PLAYER_COLORS.length]
              // Sort by live position when available, else by car number
              const sortedDrivers = [...a.drivers].sort((x, y) => {
                if (!hasLive) return x.number - y.number
                const px = liveMap.get(x.number)?.position ?? 999
                const py = liveMap.get(y.number)?.position ?? 999
                return px - py
              })
              return (
                <div
                  className="player-card"
                  key={a.playerName}
                  style={{ borderTopColor: color, borderTopWidth: 3 }}
                >
                  <div className="player-card-header">
                    <div className="player-name-badge" style={{ color }}>
                      {a.playerName}
                    </div>
                    <div
                      className="driver-count-badge"
                      onClick={onViewPlayer ? () => onViewPlayer(idx) : undefined}
                      style={onViewPlayer ? { cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' } : undefined}
                      title={onViewPlayer ? `View ${a.playerName}'s tracking page` : undefined}
                    >
                      {a.drivers.length} drivers
                    </div>
                  </div>
                  <div className="player-card-drivers">
                    {sortedDrivers.map(d => {
                      const live = liveMap.get(d.number)
                      return (
                        <div className="driver-row" key={d.number}
                          style={live?.isDNF ? { opacity: 0.5 } : undefined}>
                          <DriverAvatar name={d.name} number={d.number} color={color} size={36} />
                          <div className="driver-number-badge" style={{ background: color }}>
                            #{d.number}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="driver-name-text">{d.name}</div>
                            {d.team && <div className="driver-team-text">{d.team}</div>}
                          </div>
                          {live ? (
                            live.isDNF ? (
                              <span className="dnf-tag">DNF</span>
                            ) : (
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <div style={{
                                  fontSize: '0.82rem', fontWeight: 800,
                                  color: live.position <= 3 ? ['#d4a017','#9ca3af','#cd7f32'][live.position - 1] : '#fff',
                                }}>
                                  {live.position <= 3
                                    ? ['🥇','🥈','🥉'][live.position - 1]
                                    : `P${live.position}`}
                                </div>
                                <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)' }}>
                                  {live.speed.toFixed(1)} mph
                                </div>
                              </div>
                            )
                          ) : null}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        )
      })()}


      </div>{/* end content zIndex wrapper */}
    </div>
  )
}
